import React from 'react';
import { Home, ChevronRight, Volume2, Users, ArrowRight, ArrowLeft } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface FrederickEstherSectionProps {
  onSelectPart?: (partId: string) => void;
}

export const FrederickEstherSection: React.FC<FrederickEstherSectionProps> = ({ onSelectPart }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          
          {/* Breadcrumb: The Dentons > Part 3 of 9: Frederick Robert & Esther Denton */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPart && onSelectPart('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Dentons</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Part 3 of 9: Frederick Robert &amp; Esther Denton
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to Frederick Robert & Esther Denton"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          FREDERICK ROBERT (1837 – 1901) AND ESTHER KING DENTON (1828 – 1902)
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Users className="w-3.5 h-3.5" />
          <span>Part 3 of the 9-Part Denton Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-10 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Section 1: Arrival and Early Life at Opawa and Eyreton */}
        <section className="space-y-6">
          <p>
            Frederick Robert (Fred) was 13 when he arrived in Canterbury in December 1850 on Sir George Seymour with his family as Pilgrims, ready to farm. He travelled as a paid steerage passenger with his younger brother Edward.
          </p>

          <p>
            After the grief of the first nine months - the deaths of his mother and three siblings - Fred and Arthur remained with their father and began farming at Opawa, down to the banks of the river. They lived firstly behind canvas on a bank, until they built something better. It was a far cry from the Fishey Hall they had left behind.
          </p>

          {/* Image 60: 1852 Dullatur House */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/60 1852 Dullatur House.jpg"
              alt="1852 Dullatur House"
              caption="Dullatur, house being built for Archdeacon Wilson, completed 1852, in Opawa, showing the nature of the land the Dentons worked in."
              filenameHint="60 1852 Dullatur House.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Dullatur, house being built for Archdeacon Wilson, completed 1852, in Opawa, showing the nature of the land the Dentons worked in.
            </p>
          </div>

          <p>
            Opawa was laid out in rural sections and town sections, and soon became a favoured location for larger gentlemen’s homes on big sections that enjoyed hill and river vistas.
          </p>

          <p>
            To earn money Fred ran a carting business with a bullock team, carting stores to remote properties and returning with wool and other farm goods. His sister Alice recalled that in winter he had to stamp out the ice at the edge of a river and break the ice to persuade the bullocks into the water. He would have seen a lot of the region and got to know the favoured areas, and probably started thinking about where he would later settle, which was to be on the north side of the Waimakariri, at Eyreton.
          </p>

          <p>
            Their father John died 25 September 1956, so it was Arthur and Fred who took ownership of Opawa Rural Section 157. Arthur and Julia Wilson<sup className="font-bold text-[#7a5b3a]">[1]</sup> married on 3 March 1857. Fred married Esther King on 8 June 1857. Their lives were then to take different paths.
          </p>

          <p>
            Esther was 25<sup className="font-bold text-[#7a5b3a]">[2]</sup> when she arrived as an assisted emigrant on the sailing ship Sir Edward Paget on 2 July 1856. Eleven months later Esther (nearly 30) and almost 20-year-old Fred stood together in front of the Reverend Octavius Mathias at St Michael &amp; All Angels’ Church<sup className="font-bold text-[#7a5b3a]">[3]</sup>, in what was likely a double wedding with Esther’s friend Mary Ann Norfolk, who came out on the same sailing ship, and David Todd.<sup className="font-bold text-[#7a5b3a]">[4]</sup>
          </p>

          <p>
            After marriage Fred went into partnership with another man in a farming venture, milking cows and making butter by hand – but his partner slipped off with the surplus money. He went back into agricultural contracting and working for himself again. About 1863 he sold the Opawa land and bought his first plains block at Eyreton, calling it Fishley Farm.
          </p>

          <p>
            Over the years Fred added farms - Dungeon Creek on the north branch of the Waipara River, a block of grazing tussock land in Mt Virginia, 300ha at Lowburn and at Cabbage Tree Flat (Motunau).
          </p>

          {/* Image 61: Fishley Farm 1900 */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/61 Fishley Farm 1900.jpg"
              alt="Fishley Farm 1900"
              caption="Fishley Farm, 1900. Fred’s son Thomas (1866 – 1956) & Susannah Denton (nee Todd) with L-R Charles Frederick, Annie Esther and Nora Faith."
              filenameHint="61 Fishley Farm 1900.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Fishley Farm, 1900. Fred’s son Thomas (1866 – 1956) &amp; Susannah Denton (nee Todd) with L-R Charles Frederick, Annie Esther and Nora Faith. In 1893 he took over the farm which he sold in 1905 to move to a grazing run near Seddon, called Barewood, then other farms until he settled at Marama. They retired to Wakefield in 1944.
            </p>
          </div>

          {/* Children List */}
          <div className="space-y-3 pt-2">
            <h3 className="font-serif text-xl font-bold text-[#4a3525]">
              Thomas and Susannah’s children:
            </h3>
            
            <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2.5">
              <ul className="list-disc list-outside pl-6 space-y-2 text-[#2c221e]">
                <li><strong>Elizabeth Esther (1858 – 1886)</strong> married William Greig</li>
                <li><strong>Harry George (1859 – 1886)</strong> was badly injured when he was 18 when a dray wheel ran over him; he died aged 26</li>
                <li><strong>John Frederick (Jack) (1861 – 1902)</strong> married Eliza Jane Giles</li>
                <li><strong>Isaac Ephraim (1863 – 1948)</strong></li>
                <li><strong>Lydia Louise (1864 – 1911)</strong> married Philp Marsh, then later Jack Morgan</li>
                <li><strong>Thomas William (1866 – 1956)</strong> married Susannah Elizabeth Todd of Southbrook - theirs was the first wedding in the Anglican Church there in 1892</li>
                <li><strong>Albert Edward (Bix) (1869 – 1848)</strong> married Rebecca Gert Giles, farming at Dommett on Millburn Farm</li>
                <li><strong>Lucy Maria (1872 – 1915)</strong> looked after her ageing mother then married Thomas James Morgan</li>
              </ul>
            </div>
          </div>

          {/* Image 62: Fred Denton in older age */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/62 Fred Denton in older age.jpg"
              alt="Fred Denton in older age"
              caption="Fred Denton, died from a heart attack 17 August 1901, aged 65, while visiting relatives in the North Island."
              filenameHint="62 Fred Denton in older age.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Fred Denton, died from a heart attack 17 August 1901, aged 65, while visiting relatives in the North Island.
            </p>
          </div>

          <p>
            Esther died almost exactly a year later, 11 August 1902, at Rangiora.
          </p>

          {/* Image 63: Headstone for Fred and Esther Denton */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/63 headstone for Fred and Esther Denton at Eyreton.jpg"
              alt="Headstone for Fred and Esther Denton at Eyreton"
              caption="Headstone at St Thomas, Eyreton"
              filenameHint="63 headstone for Fred and Esther Denton at Eyreton.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Headstone at St Thomas, Eyreton
            </p>
          </div>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 2: Esther King's Family Lived in Constable Country */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              ESTHER KING’S FAMILY LIVED IN CONSTABLE COUNTRY
            </h2>
          </div>

          <p>
            Esther’s parents were Isaac King (1788- 1869) and Linda Boules<sup className="font-bold text-[#7a5b3a]">[5]</sup> (1792 – 1873) of Stoke-by- Nayland.
          </p>

          <p>
            They were married on 6 March 1810 at Stoke-by-Nayland, Suffolk. The area is often referred to as Constable Country because of the many paintings done of the area by its famous artistic son.
          </p>

          {/* Image 64: Stoke-by-Nayland Church */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/64 Stoke-by-Nayland Church.jpg"
              alt="Stoke-by-Nayland Church"
              caption="Stoke-by-Nayland by Suffolk painter John Constable (1776 – 1837) painted 1810-11, was only a few kilometres from where Constable grew up. The Stour Valley countryside was an important feature in his art."
              filenameHint="64 Stoke-by-Nayland Church.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Stoke-by-Nayland by Suffolk painter John Constable (1776 – 1837) painted 1810-11, was only a few kilometres from where Constable grew up. The Stour Valley countryside was an important feature in his art.
            </p>
          </div>

          <p>
            Esther was the 10th child.<sup className="font-bold text-[#7a5b3a]">[6]</sup> Her siblings were George, Isaac, Joseph, Samuel (born 1816), Deborah, Lydia, Mary Ann (1822-47), Abraham, Lucy, Mary Ann (born 1826), Esther, Thomas, Cornelius and Henry. Lucy (1826 – 1911) came to NZ in January 1860. She married Ephriam Jeffs in July 1860 and they lived in the Kaiapoi area so she would have been near her sister. Henry King (1837 – 1894) came to NZ, married Emily Jane Hannah Smith (1856 – 1923), and they lived in the East Eyreton area, also near Esther and Fred.
          </p>

          <p>
            Esther’s father Isaac was one of 14 children born to James King (1740 -1813) and Susanna Farrow (born 1749), who also lived in Suffolk. Susanna was the only child of Abraham Farrow (born 1725) and Mary (born 1727).<sup className="font-bold text-[#7a5b3a]">[7]</sup> Her mother Linda (1792 – 1873) was born at Wormingford in Essex and died in Bavaria at Bayernm Staudach – Egerndach. She was the only child of Henry Boules, sometimes spelt Bowles (1771 – 1847) of Essex and Mary Mills (born 1771). Wormingford is on the south side of the River Stour, with lovely walking trails such as the Stour Valley Path amidst gentle rolling countryside.
          </p>

          <p>
            Suffolk had an important woolen cloth industry for centuries. The Suffolk sheep breed originated in the area around Bury St Edmunds, where the Dentons came from. They were a cross between Norfolk Horn ewes (below) and Southdown rams.
          </p>

          <p>
            Anne Maria Pryke (the future Mrs Federick Robert Denton) was born at Kersey, Suffolk below), a centre for producing a thick woollen cloth known as Kersey - “Autumn Ginger” pictured below.
          </p>

          <p>
            Samuel Butler came to Canterbury (1860) after Cambridge University.<sup className="font-bold text-[#7a5b3a]">[8]</sup> He took up a large sheep run in the upper Rangitata, Erewhon - he later wrote “The Way of All Flesh” back in England.
          </p>

          {/* Samuel Butler Quotes */}
          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-4 shadow-xs">
            <p className="italic">
              “…we reached Christ Church on the little river Avon; it is larger than Lyttelton and more scattered, but not so pretty. Here, too, the men are shaggy, clear-complexioned, brown and healthy-looking, and wear exceedingly rowdy hats. I put up at Mr Roland Davis’s<sup className="font-bold text-[#7a5b3a] not-italic">[9]</sup>; and as no one during the evening seemed much inclined to talk to me, I listened to the conversation.
            </p>
            <p className="italic">
              The all-engrossed topics seemed to be sheep, horses, dogs, cattle, English grasses, paddocks, bush and so forth. From about seven o’clock in the evening till about twelve at night I cannot say that I heard much else. These were the exact things I wanted to hear about, and I listened till they had been repeated so many times over that I almost grew tired of the subject, and wished the conversation would turn to something else.
            </p>
            <p className="italic">
              A few expressions were not familiar to me. When we would say in England ‘certainly not’ it is here ‘no fear’ or ‘don’t you believe it’. When they want to answer in the affirmative they say it is so’, ‘it does so’. The word ‘hum’ too, without pronouncing the u, is in amusing requisition. I perceived that this stood either for assent, or doubt, or wonder, or general expression of comprehension without compromising the hummer’s own opinion, and indeed for a great many more things than these; in fact, if a man did not want to say anything at all he said ‘hum hum’. It is a very good expression, and saves much trouble when its familiar use has be acquired. Beyond these trifles I noticed no Yankeeism, and the conversation was English in point of expression.
            </p>
            <p className="italic">
              I was rather startled at hearing one gentleman ask another whether he meant to wash this year, and receive the answer ‘no.’ I soon discovered that a person’s sheep are himself. If his sheep are clean, he is clean. He does not wash his sheep before shearing, but he washes; and most marvellous of all, it is not his sheep which lamb but he ‘lambs down’ himself.” (27 January 1860)
            </p>
          </blockquote>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-4 shadow-xs">
            <p className="italic">
              “New Zealand seems far better adapted to develop and maintain in health the physical than the intellectual nature. The fact is, people here are busy making money; that is the inducement which led them to come here in the first instance, and they show their sense by devoting their energies to the work. Yet after all, it may be questioned whether the intellect is not as well schooled here as at home, though in a very different manner. Men are as shrewd and sensible as alive to the humorous, and as hard headed. Moreover, there is much nonsense in the old country from which people here are free. There is little conventionalism, little formality, and much liberality of sentiment; very little sectarianism, and, as a general rule, a healthy sensible tone in conversation, which I like much. (24 March 1860)
            </p>
          </blockquote>

          {/* Image 65: Lyttelton Hotel */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/65 Lyttelton Hotel.jpg"
              alt="Lyttelton Hotel"
              caption="The Lyttelton Hotel on the right (1870) - demolished in 1903. It was the site of the Clarendon Hotel, on the corner of Oxford Terrace and Worcester Street."
              filenameHint="65 Lyttelton Hotel.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              The Lyttelton Hotel on the right (1870) - demolished in 1903. It was the site of the Clarendon Hotel, on the corner of Oxford Terrace and Worcester Street. Davis first called it the Davis<sup className="font-bold text-[#7a5b3a] not-italic">[10]</sup> Hotel buy changed the name to Lyttelton Hotel in 1860. The first house on the site was built by William Guise Brittan. Photo: Christchurch City Libraries Ref CCL Photo ID 4 IMG 0089
            </p>
          </div>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-4 shadow-xs">
            <p className="italic">
              “I should be loath to advise any gentleman to come out here, unless he have either money and an average share of good sense, or else a large amount of proper self-respect and strength of purpose. If a young man goes out to friends, on an arrangement definitely settled before he leaves England, he is at any rate certain of employment and of a home upon his landing here; but if he lands friendless, or simply the bearer of a few letters of introduction, obtained from second or third hand – because his cousin knew somebody who had a friend who had married a lady whose nephew was somewhere in NZ – he has no very enviable look-out upon his arrival.” (June 1860)
            </p>
          </blockquote>
        </section>

        {/* Footnotes Section */}
        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />
        
        <section className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border border-[#d8ceb9] space-y-3 text-xs sm:text-sm font-serif text-[#5c4328]">
          <h4 className="font-bold uppercase tracking-wider text-[#7a5b3a] text-xs">
            Footnotes &amp; Archival References
          </h4>
          <ol className="list-decimal list-outside pl-5 space-y-1.5">
            <li>Julia Wilson, born 3 March 1839 at Worksop (near Sheffield), England, came to NZ on Sir George Pollock, arriving January 1951</li>
            <li>The ship’s passenger list states her age as 25</li>
            <li>At that time it was called Christ’s Church, a very simple building on Oxford Tce/Durham Street South corner</li>
            <li>Their daughter Susannah Elizabeth Todd married Esther’s son Thomas William Denton</li>
            <li>Also spelt Bowles and Bowley in various records</li>
            <li>Family Search data</li>
            <li>This is as far back as Family Search records went</li>
            <li>“A First Year in Canterbury Settlement” 1863</li>
            <li>Rowland Robert Teape Davis (1806 -79) was from County Cork, who came to NZ in 1840, had hotels as well as being a leading figure in working class causes and calls for reforms. He had the Lyttelton Hotel in Christchurch 1850-64.</li>
            <li>Davis had represented Lyttelton on the Provincial Government for many years but shifted to Christchurch. He came to NZ on the same ship as John Deans.</li>
          </ol>
        </section>

      </article>

      {/* Chapter Bottom Navigation */}
      <footer className="flex flex-col sm:flex-row justify-between items-center gap-4 p-5 sm:p-6 bg-[#f3ede0] rounded-3xl border-2 border-[#d8ceb9] shadow-sm">
        <button
          onClick={() => onSelectPart && onSelectPart('part2')}
          className="flex items-center gap-2 text-xs sm:text-sm font-bold text-[#7a5b3a] hover:text-[#2c221e] hover:underline font-serif transition cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Previous: Part 2 — What happened to the first generation Dentons</span>
        </button>
        <button
          onClick={() => onSelectPart && onSelectPart('part4')}
          className="flex items-center gap-2 text-xs sm:text-sm font-bold text-[#7a5b3a] hover:text-[#2c221e] hover:underline font-serif transition cursor-pointer"
        >
          <span>Next: Part 4 — John Frederick &amp; Eliza DENTON</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </footer>

    </div>
  );
};
